<script setup lang="ts">
import { ref } from 'vue';
import { useKeysStore } from '../stores/keys';

const emit = defineEmits<{ (e: 'close'): void }>();

const keys = useKeysStore();
const name = ref('');
const rawKey = ref<string | null>(null);
const error = ref<string | null>(null);
const busy = ref(false);
const copied = ref(false);

async function submit(): Promise<void> {
  busy.value = true;
  error.value = null;
  try {
    const res = await keys.create(name.value.trim() || null);
    rawKey.value = res.raw_key;
  } catch (err) {
    error.value = (err as Error).message;
  } finally {
    busy.value = false;
  }
}

async function copy(): Promise<void> {
  if (!rawKey.value) return;
  try {
    await navigator.clipboard.writeText(rawKey.value);
    copied.value = true;
    window.setTimeout(() => (copied.value = false), 2000);
  } catch {
    /* clipboard may be unavailable on insecure origins; ignore */
  }
}
</script>

<template>
  <div
    class="fixed inset-0 bg-black/60 flex items-center justify-center px-4 z-50"
    @click.self="emit('close')"
  >
    <div class="card max-w-md w-full p-6">
      <div class="flex items-center justify-between mb-4">
        <h2 class="font-semibold">Create API key</h2>
        <button class="text-muted hover:text-ink" @click="emit('close')">✕</button>
      </div>

      <div v-if="!rawKey">
        <label class="text-xs text-muted block mb-1" for="key-name">Name (optional)</label>
        <input
          id="key-name"
          v-model="name"
          class="input w-full"
          type="text"
          maxlength="80"
          placeholder="e.g. production backend"
          :disabled="busy"
          @keyup.enter="submit"
        />
        <p v-if="error" class="text-danger text-xs mt-2">{{ error }}</p>
        <div class="flex justify-end gap-2 mt-5">
          <button class="btn-ghost" :disabled="busy" @click="emit('close')">Cancel</button>
          <button class="btn-primary" :disabled="busy" @click="submit">
            {{ busy ? 'Creating…' : 'Create key' }}
          </button>
        </div>
      </div>

      <div v-else>
        <p class="text-sm mb-3">
          Copy your key now. <strong>It will not be shown again.</strong>
        </p>
        <div class="bg-canvas border border-border rounded p-3 font-mono text-xs break-all">
          {{ rawKey }}
        </div>
        <div class="flex justify-end gap-2 mt-5">
          <button class="btn-ghost" @click="copy">{{ copied ? 'Copied ✓' : 'Copy' }}</button>
          <button class="btn-primary" @click="emit('close')">I've stored it</button>
        </div>
      </div>
    </div>
  </div>
</template>
